import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Code,
  Key,
  Webhook,
  Database,
  Terminal,
  FileJson,
  Settings,
  ExternalLink,
  Copy,
  CheckCircle,
  Image,
  Link,
  Globe,
  Smartphone,
  Save,
  Loader2,
  RefreshCw,
  CreditCard,
  Gauge,
  Download,
  Upload,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import StripeManager from '../components/admin/StripeManager';
import SMTPManager from '../components/admin/SMTPManager';
import StorageManager from '../components/admin/StorageManager';
import SiteRulesManager from '../components/admin/SiteRulesManager';
import SystemManager from '../components/admin/SystemManager';
import MediaManager from '../components/admin/MediaManager';
import ServerMonitor from '../components/admin/ServerMonitor';
import WebhookManager from '../components/admin/WebhookManager';
import LogViewer from '../components/admin/LogViewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar menu items
const sidebarItems = [
  { id: 'general', label: 'General Settings', icon: Settings },
  { id: 'media', label: 'Media', icon: Image },
  { id: 'stripe', label: 'Stripe', icon: CreditCard },
  { id: 'smtp', label: 'SMTP', icon: Globe },
  { id: 'storage', label: 'Storage', icon: Database },
  { id: 'site-rules', label: 'Site Rules', icon: FileJson },
  { id: 'system', label: 'System', icon: Terminal },
  { id: 'server-monitor', label: 'Server Monitor', icon: Gauge },
  { id: 'overview', label: 'Overview', icon: Code },
  { id: 'api-keys', label: 'API Keys', icon: Key },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'logs', label: 'Logs', icon: Terminal },
  { id: 'exports', label: 'Data Exports', icon: FileJson },
];

export default function DeveloperSettingsPage() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [activeSection, setActiveSection] = useState('general');
  const [copied, setCopied] = useState(false);
  
  // General settings state
  const [brandingSettings, setBrandingSettings] = useState({
    logo_url: '',
    logo_link_url: '',
    dashboard_logo_url: '',
    dashboard_logo_link: '',
    favicon_url: '',
    pwa_icon_url: ''
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Export/Import state
  const [exportStats, setExportStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Theme-aware classes
  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-gray-50';
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const bgTertiary = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  // Fetch branding settings on mount
  useEffect(() => {
    const fetchBrandingSettings = async () => {
      try {
        const response = await axios.get(`${API}/admin/branding-settings`);
        setBrandingSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch branding settings:', error);
      } finally {
        setLoadingSettings(false);
      }
    };
    
    if (isAdmin) {
      fetchBrandingSettings();
    }
  }, [isAdmin]);

  // Redirect non-admins after auth check is complete
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/inbox');
    }
  }, [authLoading, isAdmin, navigate]);

  const saveBrandingSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/branding-settings`, brandingSettings);
      toast.success('Branding settings saved successfully');
    } catch (error) {
      toast.error('Failed to save branding settings');
      console.error(error);
    } finally {
      setSavingSettings(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  // Load export stats
  const loadExportStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/migration/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExportStats(response.data);
    } catch (error) {
      toast.error('Failed to load database stats');
    }
  };

  // Handle database export
  const handleExportDatabase = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/migration/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `mypatriotmail_backup_${new Date().toISOString().split('T')[0]}.json`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Database exported successfully');
    } catch (error) {
      toast.error('Failed to export database');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  // Handle database import
  const handleImportDatabase = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/migration/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      toast.success(`Import complete: ${response.data.total_imported} records imported`);
      loadExportStats(); // Refresh stats
    } catch (error) {
      toast.error('Failed to import database');
      console.error(error);
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  // Load stats when exports section is active
  useEffect(() => {
    if (activeSection === 'exports' && isAdmin && !exportStats) {
      loadExportStats();
    }
  }, [activeSection, isAdmin]);

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#16162a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF]"></div>
      </div>
    );
  }

  // Redirect non-admins (using useEffect pattern to avoid render-time navigation)
  if (!isAdmin) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#16162a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D4AFF] mx-auto mb-4"></div>
          <p className="text-gray-400">Redirecting...</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className={`text-2xl font-bold ${textPrimary}`}>General Settings</h2>
                <p className={`${textMuted} mt-1`}>Configure branding and logo settings</p>
              </div>
              <Button
                onClick={saveBrandingSettings}
                disabled={savingSettings}
                className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                data-testid="save-branding-btn"
              >
                {savingSettings ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Save Changes</>
                )}
              </Button>
            </div>

            {loadingSettings ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Public Logo */}
                <Card className={`${bgSecondary} ${borderColor}`}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#6D4AFF]/20 rounded-lg">
                        <Image className="w-5 h-5 text-[#6D4AFF]" />
                      </div>
                      <div>
                        <CardTitle className={textPrimary}>Public Logo</CardTitle>
                        <CardDescription className={textMuted}>Logo displayed on public pages</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className={textSecondary}>Logo URL</Label>
                          <Input
                            value={brandingSettings.logo_url}
                            onChange={(e) => setBrandingSettings({ ...brandingSettings, logo_url: e.target.value })}
                            placeholder="https://example.com/logo.png"
                            className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                            data-testid="logo-url-input"
                          />
                          <p className={`text-xs ${textMuted}`}>Public logo image URL</p>
                        </div>
                        <div className="space-y-2">
                          <Label className={textSecondary}>Logo Link URL</Label>
                          <Input
                            value={brandingSettings.logo_link_url}
                            onChange={(e) => setBrandingSettings({ ...brandingSettings, logo_link_url: e.target.value })}
                            placeholder="https://mypatriotmail.org"
                            className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                            data-testid="logo-link-input"
                          />
                          <p className={`text-xs ${textMuted}`}>Where the logo links to when clicked</p>
                        </div>
                      </div>
                      <div className={`p-4 ${bgTertiary} rounded-lg flex flex-col items-center justify-center min-h-[150px]`}>
                        <p className={`text-xs ${textMuted} mb-3`}>Logo preview</p>
                        {brandingSettings.logo_url ? (
                          <img 
                            src={brandingSettings.logo_url} 
                            alt="Logo preview" 
                            className="max-h-[100px] max-w-full object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className={`text-center ${textMuted}`}>
                            <Image className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No logo set</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Dashboard Logo */}
                <Card className={`${bgSecondary} ${borderColor}`}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500/20 rounded-lg">
                        <Globe className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <CardTitle className={textPrimary}>Dashboard Logo</CardTitle>
                        <CardDescription className={textMuted}>Logo displayed in the dashboard header</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className={textSecondary}>Dashboard Logo URL</Label>
                          <Input
                            value={brandingSettings.dashboard_logo_url}
                            onChange={(e) => setBrandingSettings({ ...brandingSettings, dashboard_logo_url: e.target.value })}
                            placeholder="https://example.com/dashboard-logo.png"
                            className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                            data-testid="dashboard-logo-url-input"
                          />
                          <p className={`text-xs ${textMuted}`}>Logo shown in the dashboard</p>
                        </div>
                        <div className="space-y-2">
                          <Label className={textSecondary}>Dashboard Logo Link</Label>
                          <Input
                            value={brandingSettings.dashboard_logo_link}
                            onChange={(e) => setBrandingSettings({ ...brandingSettings, dashboard_logo_link: e.target.value })}
                            placeholder="https://mypatriotmail.org/inbox"
                            className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                            data-testid="dashboard-logo-link-input"
                          />
                          <p className={`text-xs ${textMuted}`}>Where dashboard logo links to</p>
                        </div>
                      </div>
                      <div className={`p-4 ${bgTertiary} rounded-lg flex flex-col items-center justify-center min-h-[150px]`}>
                        <p className={`text-xs ${textMuted} mb-3`}>Dashboard logo preview</p>
                        {brandingSettings.dashboard_logo_url ? (
                          <img 
                            src={brandingSettings.dashboard_logo_url} 
                            alt="Dashboard logo preview" 
                            className="max-h-[100px] max-w-full object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className={`text-center ${textMuted}`}>
                            <Globe className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No dashboard logo set</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Favicon */}
                <Card className={`${bgSecondary} ${borderColor}`}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-500/20 rounded-lg">
                        <Link className="w-5 h-5 text-orange-500" />
                      </div>
                      <div>
                        <CardTitle className={textPrimary}>Favicon</CardTitle>
                        <CardDescription className={textMuted}>Browser tab icon</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className={textSecondary}>Favicon URL</Label>
                        <Input
                          value={brandingSettings.favicon_url}
                          onChange={(e) => setBrandingSettings({ ...brandingSettings, favicon_url: e.target.value })}
                          placeholder="https://example.com/favicon.ico"
                          className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                          data-testid="favicon-url-input"
                        />
                        <p className={`text-xs ${textMuted}`}>Browser tab icon (32x32 recommended)</p>
                      </div>
                      <div className={`p-4 ${bgTertiary} rounded-lg flex flex-col items-center justify-center min-h-[100px]`}>
                        <p className={`text-xs ${textMuted} mb-3`}>Favicon preview</p>
                        {brandingSettings.favicon_url ? (
                          <img 
                            src={brandingSettings.favicon_url} 
                            alt="Favicon preview" 
                            className="w-8 h-8 object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className={`text-center ${textMuted}`}>
                            <Link className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No favicon set</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* PWA Icon */}
                <Card className={`${bgSecondary} ${borderColor}`}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Smartphone className="w-5 h-5 text-purple-500" />
                      </div>
                      <div>
                        <CardTitle className={textPrimary}>PWA Icon</CardTitle>
                        <CardDescription className={textMuted}>App icon for mobile devices</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className={textSecondary}>PWA Icon URL</Label>
                        <Input
                          value={brandingSettings.pwa_icon_url}
                          onChange={(e) => setBrandingSettings({ ...brandingSettings, pwa_icon_url: e.target.value })}
                          placeholder="https://example.com/pwa-icon-192.png"
                          className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                          data-testid="pwa-icon-url-input"
                        />
                        <p className={`text-xs ${textMuted}`}>App icon for mobile (192x192 recommended)</p>
                      </div>
                      <div className={`p-4 ${bgTertiary} rounded-lg flex flex-col items-center justify-center min-h-[100px]`}>
                        <p className={`text-xs ${textMuted} mb-3`}>PWA icon preview</p>
                        {brandingSettings.pwa_icon_url ? (
                          <img 
                            src={brandingSettings.pwa_icon_url} 
                            alt="PWA icon preview" 
                            className="w-16 h-16 object-contain rounded-xl"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className={`text-center ${textMuted}`}>
                            <Smartphone className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No PWA icon set</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        );

      case 'media':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Media Library</h2>
              <p className={`${textMuted} mt-1`}>Manage platform images and assets</p>
            </div>
            <MediaManager />
          </div>
        );

      case 'stripe':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Stripe Integration</h2>
              <p className={`${textMuted} mt-1`}>Configure payment processing settings</p>
            </div>
            <StripeManager />
          </div>
        );

      case 'smtp':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>SMTP Configuration</h2>
              <p className={`${textMuted} mt-1`}>Configure email sending settings</p>
            </div>
            <SMTPManager />
          </div>
        );

      case 'storage':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Storage Configuration</h2>
              <p className={`${textMuted} mt-1`}>Configure cloud storage for email attachments</p>
            </div>
            <StorageManager />
          </div>
        );

      case 'site-rules':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Site Rules</h2>
              <p className={`${textMuted} mt-1`}>Platform rules and business logic documentation</p>
            </div>
            <SiteRulesManager />
          </div>
        );

      case 'system':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>System</h2>
              <p className={`${textMuted} mt-1`}>Cache management and system information</p>
            </div>
            <SystemManager />
          </div>
        );

      case 'server-monitor':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Server Monitor</h2>
              <p className={`${textMuted} mt-1`}>Real-time server resource usage and performance metrics</p>
            </div>
            <ServerMonitor isDarkMode={isDarkMode} />
          </div>
        );

      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Developer Overview</h2>
              <p className={`${textMuted} mt-1`}>Access developer tools and API documentation</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className={`${bgSecondary} ${borderColor} hover:border-[#6D4AFF]/50 transition-colors cursor-pointer`}
                onClick={() => setActiveSection('api-keys')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-[#6D4AFF]/20 rounded-lg">
                      <Key className="w-6 h-6 text-[#6D4AFF]" />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>API Keys</h3>
                      <p className={`text-sm ${textMuted}`}>Manage API access tokens</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`${bgSecondary} ${borderColor} hover:border-[#6D4AFF]/50 transition-colors cursor-pointer`}
                onClick={() => setActiveSection('webhooks')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-500/20 rounded-lg">
                      <Webhook className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>Webhooks</h3>
                      <p className={`text-sm ${textMuted}`}>Configure event notifications</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`${bgSecondary} ${borderColor} hover:border-[#6D4AFF]/50 transition-colors cursor-pointer`}
                onClick={() => setActiveSection('database')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      <Database className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>Database</h3>
                      <p className={`text-sm ${textMuted}`}>View database stats</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`${bgSecondary} ${borderColor} hover:border-[#6D4AFF]/50 transition-colors cursor-pointer`}
                onClick={() => setActiveSection('logs')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-500/20 rounded-lg">
                      <Terminal className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>Logs</h3>
                      <p className={`text-sm ${textMuted}`}>View application logs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`${bgSecondary} ${borderColor} hover:border-[#6D4AFF]/50 transition-colors cursor-pointer`}
                onClick={() => setActiveSection('exports')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <FileJson className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>Data Exports</h3>
                      <p className={`text-sm ${textMuted}`}>Export system data</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Info */}
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardHeader>
                <CardTitle className={textPrimary}>Quick Reference</CardTitle>
                <CardDescription className={textMuted}>Common endpoints and configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`p-4 ${bgTertiary} rounded-lg`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${textSecondary}`}>API Base URL</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(process.env.REACT_APP_BACKEND_URL + '/api')}
                      className={textMuted}
                    >
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <code className={`text-sm ${textPrimary} break-all`}>
                    {process.env.REACT_APP_BACKEND_URL}/api
                  </code>
                </div>
                
                <div className={`p-4 ${bgTertiary} rounded-lg`}>
                  <span className={`text-sm font-medium ${textSecondary}`}>Documentation</span>
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(process.env.REACT_APP_BACKEND_URL + '/docs', '_blank')}
                      className={`${borderColor} ${textSecondary}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Swagger Docs
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(process.env.REACT_APP_BACKEND_URL + '/redoc', '_blank')}
                      className={`${borderColor} ${textSecondary}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      ReDoc
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'api-keys':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>API Keys</h2>
              <p className={`${textMuted} mt-1`}>Manage API access tokens for external integrations</p>
            </div>
            
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardContent className="p-6">
                <div className={`p-8 ${bgTertiary} rounded-lg text-center`}>
                  <Key className={`w-12 h-12 mx-auto mb-4 ${textMuted}`} />
                  <h3 className={`font-semibold ${textPrimary} mb-2`}>API Keys Coming Soon</h3>
                  <p className={`text-sm ${textMuted}`}>
                    Generate and manage API keys for programmatic access to MyPatriotMail.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'webhooks':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Webhooks</h2>
              <p className={`${textMuted} mt-1`}>Manage Stripe webhook endpoints and view event logs</p>
            </div>
            <WebhookManager />
          </div>
        );

      case 'logs':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Application Logs</h2>
              <p className={`${textMuted} mt-1`}>View and search through application logs</p>
            </div>
            <LogViewer />
          </div>
        );

      case 'exports':
        return (
          <div className="space-y-6">
            <div>
              <h2 className={`text-2xl font-bold ${textPrimary}`}>Database Migration</h2>
              <p className={`${textMuted} mt-1`}>Export and import your entire database for migration</p>
            </div>
            
            {/* Stats */}
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardContent className="p-6">
                <h3 className={`font-semibold ${textPrimary} mb-4`}>Database Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-4 ${bgTertiary} rounded-lg text-center`}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{exportStats?.total_documents || '...'}</p>
                    <p className={`text-sm ${textMuted}`}>Total Documents</p>
                  </div>
                  <div className={`p-4 ${bgTertiary} rounded-lg text-center`}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{exportStats?.collections ? Object.keys(exportStats.collections).length : '...'}</p>
                    <p className={`text-sm ${textMuted}`}>Collections</p>
                  </div>
                  <div className={`p-4 ${bgTertiary} rounded-lg text-center`}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{exportStats?.collections?.users || '...'}</p>
                    <p className={`text-sm ${textMuted}`}>Users</p>
                  </div>
                  <div className={`p-4 ${bgTertiary} rounded-lg text-center`}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{exportStats?.collections?.emails || '...'}</p>
                    <p className={`text-sm ${textMuted}`}>Emails</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={loadExportStats}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Stats
                </Button>
              </CardContent>
            </Card>

            {/* Export */}
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <Download className="w-6 h-6 text-green-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${textPrimary}`}>Export Full Database</h3>
                    <p className={`text-sm ${textMuted} mt-1`}>
                      Download your entire database as a JSON file. Includes all users, emails, 
                      subscriptions, walls, contacts, and settings.
                    </p>
                    <Button 
                      className="mt-4 bg-green-600 hover:bg-green-700"
                      onClick={handleExportDatabase}
                      disabled={exporting}
                    >
                      {exporting ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download Full Backup
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Import */}
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <Upload className="w-6 h-6 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${textPrimary}`}>Import Database</h3>
                    <p className={`text-sm ${textMuted} mt-1`}>
                      Upload a previously exported JSON backup. New records will be added, 
                      existing records (by ID) will be skipped.
                    </p>
                    <div className="mt-4">
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImportDatabase}
                        className="hidden"
                        id="import-file"
                        disabled={importing}
                      />
                      <label htmlFor="import-file">
                        <Button 
                          asChild
                          variant="outline"
                          className="cursor-pointer"
                          disabled={importing}
                        >
                          <span>
                            {importing ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Importing...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Backup File
                              </>
                            )}
                          </span>
                        </Button>
                      </label>
                    </div>
                    {importResult && (
                      <div className={`mt-4 p-4 ${bgTertiary} rounded-lg`}>
                        <p className={`font-medium ${textPrimary}`}>Import Complete</p>
                        <p className={`text-sm ${textMuted}`}>
                          Imported: {importResult.total_imported} | Skipped: {importResult.total_skipped}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Warning */}
            <Card className={`border-yellow-500/50 ${bgSecondary}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                  <div>
                    <h3 className={`font-semibold ${textPrimary}`}>Migration Notes</h3>
                    <ul className={`text-sm ${textMuted} mt-2 space-y-1 list-disc list-inside`}>
                      <li>Export includes sensitive data (hashed passwords, emails) - keep secure</li>
                      <li>Import merges data - existing records with same ID are skipped</li>
                      <li>After import, verify user logins and subscription status</li>
                      <li>Attachments stored in iDrive must be migrated separately</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${bgPrimary}`}>
      {/* Header */}
      <header className={`${bgSecondary} border-b ${borderColor} px-6 py-4`}>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/inbox')}
            className={`${textMuted} hover:${textPrimary}`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#6D4AFF]/20 rounded-lg">
              <Code className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div>
              <h1 className={`text-xl font-semibold ${textPrimary}`}>Developer Settings</h1>
              <p className={`text-sm ${textMuted}`}>API access, webhooks, and developer tools</p>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`w-64 ${bgSecondary} border-r ${borderColor} min-h-[calc(100vh-73px)] p-4`}>
          <nav className="space-y-1">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  data-testid={`sidebar-${item.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    isActive
                      ? 'bg-[#6D4AFF] text-white'
                      : `${textSecondary} hover:${bgTertiary} hover:${textPrimary}`
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
          
          {/* Sidebar Footer */}
          <div className={`mt-6 p-4 ${bgTertiary} rounded-lg`}>
            <p className={`text-xs ${textMuted} mb-2`}>Logged in as</p>
            <p className={`text-sm font-medium ${textPrimary} truncate`}>{user?.email}</p>
            <Badge className="mt-2 bg-[#6D4AFF]/20 text-[#6D4AFF]">Admin</Badge>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
